import { Component, forwardRef, input, output, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';
import { mimeToExtension } from '../../utils/file-types/file-types';

@Component({
  selector: 'app-file-upload',
  imports: [NgClass, IconComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.css',
})
export class FileUploadComponent implements ControlValueAccessor {
  public accept = input('*');
  public fileChange = output<File | null>();
  public fileName = signal('');
  public isDragOver = signal(false);
  private onChange: (file: File | null) => void = () => {
    // Replaced with actual implementation in registerOnChange
  };
  private onTouched: () => void = () => {
    // Replace with actual implementation in registerOnTouched
  };
  protected error = signal(false);

  writeValue(file: File | null): void {
    this.fileName.set(file?.name || '');
  }

  registerOnChange(fn: (file: File | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  handleFile(file: File) {
    const isInvalid = !this.accept()
      .split(',')
      .map((type) => type.trim())
      .includes(mimeToExtension(file.type) || '');
    this.error.set(isInvalid);
    if (isInvalid) return;
    this.fileName.set(file.name);
    this.onChange(file); // Reactive forms
    this.onTouched(); // Reactive forms
    this.fileChange.emit(file); // Non-reactive usage
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver.set(false);

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  clearFile() {
    const input = document.querySelector('.file-input') as HTMLInputElement;
    if (input) {
      input.value = '';
      this.fileName.set('');
      this.onChange(null);
      this.fileChange.emit(null);
    }
  }
}
